// Freeze endpoints (plan §4, spec J5 and J10; T32). The task and evidence pages switch to the
// frozen view with `?freeze=<id>` in the page URL and read it from /freezes/:id/tasks/:taskId or
// /freezes/:id/evidence/:code.
import { API_BASE_PATH, type Http } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  afterFreezeSchema,
  freezeDetailSchema,
  freezeListSchema,
  frozenViewSchema,
  type AfterFreeze,
  type FreezeDetail,
  type FreezeList,
  type FrozenView,
} from './schemas/freeze.ts';

export * from './schemas/freeze.ts';

export type FreezeEntityType = 'task' | 'evidence_line';

export interface FreezeApi {
  list(page?: number, signal?: AbortSignal): Promise<FreezeList>;
  get(id: number, signal?: AbortSignal): Promise<FreezeDetail>;
  /** Owner only. The key makes a double submit create one freeze (constitution §4.12). */
  create(label: string, idempotencyKey: string): Promise<FreezeDetail>;
  afterFreeze(type: FreezeEntityType, key: string, signal?: AbortSignal): Promise<AfterFreeze>;
  frozenTask(id: number, taskId: string, signal?: AbortSignal): Promise<FrozenView>;
  frozenEvidence(id: number, code: string, signal?: AbortSignal): Promise<FrozenView>;
}

const LIST_PAGE_SIZE = 50;

/** Link for the snapshot file download (owner and Leads roles). A plain GET, no script needed. */
export function freezeSnapshotUrl(id: number, base = API_BASE_PATH): string {
  return `${base}/freezes/${pathSegment(id)}/snapshot`;
}

export function createFreezeApi(http: Http): FreezeApi {
  return {
    list: (page = 1, signal) =>
      http.json(
        {
          method: 'GET',
          path: `/freezes?page=${String(page)}&pageSize=${String(LIST_PAGE_SIZE)}`,
          signal,
        },
        freezeListSchema,
      ),
    get: (id, signal) =>
      http.json({ method: 'GET', path: `/freezes/${pathSegment(id)}`, signal }, freezeDetailSchema),
    create: (label, idempotencyKey) =>
      http.json(
        { method: 'POST', path: '/freezes', body: { label }, idempotencyKey },
        freezeDetailSchema,
      ),
    afterFreeze: (type, key, signal) =>
      http.json(
        {
          method: 'GET',
          path: `/freezes/after-freeze?entityType=${type}&key=${encodeURIComponent(key)}`,
          signal,
        },
        afterFreezeSchema,
      ),
    frozenTask: (id, taskId, signal) =>
      http.json(
        {
          method: 'GET',
          path: `/freezes/${pathSegment(id)}/tasks/${pathSegment(taskId)}`,
          signal,
        },
        frozenViewSchema,
      ),
    frozenEvidence: (id, code, signal) =>
      http.json(
        {
          method: 'GET',
          path: `/freezes/${pathSegment(id)}/evidence/${pathSegment(code)}`,
          signal,
        },
        frozenViewSchema,
      ),
  };
}
